import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Alert,
  Typography,
  Space,
  Divider,
  Spin,
  message,
} from "antd";
import {
  DeleteOutlined,
  ReloadOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import cacheApi from "../../services/cacheApi";

const { Title, Text } = Typography;
const { Option } = Select;

const CacheManagementPage: React.FC = () => {
  const [cacheNames, setCacheNames] = useState<string[]>([]);
  const [selectedCache, setSelectedCache] = useState<string>("");
  const [cacheKey, setCacheKey] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCacheNames();
  }, []);

  const fetchCacheNames = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cacheApi.getAllCacheNames();
      if (response.status === "SUCCESS" && response.data) {
        setCacheNames(response.data);
      } else {
        setError(response.message || "Failed to fetch cache names");
      }
    } catch (err) {
      setError("Failed to fetch cache names. Please try again later.");
      console.error("Error fetching cache names:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    if (!selectedCache) {
      message.error("Please select a cache to clear");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await cacheApi.clearCacheByName(selectedCache);

      if (response.status === "SUCCESS") {
        setSuccess(
          response.message || `Successfully cleared cache: ${selectedCache}`
        );
        message.success(`Cache '${selectedCache}' cleared successfully`);
      } else {
        setError(response.message || `Failed to clear cache: ${selectedCache}`);
        message.error(`Failed to clear cache: ${selectedCache}`);
      }
    } catch (err) {
      setError(
        `Failed to clear cache: ${selectedCache}. Please try again later.`
      );
      message.error(`Failed to clear cache: ${selectedCache}`);
      console.error("Error clearing cache:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCacheKey = async () => {
    if (!selectedCache) {
      message.error("Please select a cache");
      return;
    }

    if (!cacheKey) {
      message.error("Please enter a cache key");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await cacheApi.clearCacheByNameAndKey(
        selectedCache,
        cacheKey
      );

      if (response.status === "SUCCESS") {
        setSuccess(
          response.message ||
            `Successfully cleared key '${cacheKey}' from cache: ${selectedCache}`
        );
        message.success(
          `Key '${cacheKey}' cleared from cache '${selectedCache}' successfully`
        );
        setCacheKey(""); // Clear the key input after successful clear
      } else {
        setError(
          response.message ||
            `Failed to clear key '${cacheKey}' from cache: ${selectedCache}`
        );
        message.error(
          `Failed to clear key '${cacheKey}' from cache: ${selectedCache}`
        );
      }
    } catch (err) {
      setError(
        `Failed to clear key '${cacheKey}' from cache: ${selectedCache}. Please try again later.`
      );
      message.error(
        `Failed to clear key '${cacheKey}' from cache: ${selectedCache}`
      );
      console.error("Error clearing cache key:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllCaches = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await cacheApi.clearAllCaches();

      if (response.status === "SUCCESS") {
        setSuccess(response.message || "Successfully cleared all caches");
        message.success("All caches cleared successfully");
      } else {
        setError(response.message || "Failed to clear all caches");
        message.error("Failed to clear all caches");
      }
    } catch (err) {
      setError("Failed to clear all caches. Please try again later.");
      message.error("Failed to clear all caches");
      console.error("Error clearing all caches:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Cache Management</Title>
      <Text type="secondary">
        Manage application caches by clearing specific caches or individual keys
        within a cache.
      </Text>

      <Divider />

      <Spin spinning={loading}>
        <Card>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Space direction="vertical" style={{ width: "100%" }}>
                {error && (
                  <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                  />
                )}
                {success && (
                  <Alert
                    message={success}
                    type="success"
                    showIcon
                    closable
                    onClose={() => setSuccess(null)}
                  />
                )}
              </Space>
            </Col>

            <Col span={24}>
              <Card title="Cache Selection" size="small">
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} md={16}>
                    <Select
                      placeholder="Select Cache"
                      style={{ width: "100%" }}
                      value={selectedCache || undefined}
                      onChange={(value) => setSelectedCache(value)}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        !!option &&
                        !!option.children &&
                        option.children
                          .toString()
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {cacheNames.map((cacheName) => (
                        <Option key={cacheName} value={cacheName}>
                          {cacheName}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={8}>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={fetchCacheNames}
                      style={{ marginRight: 8 }}
                    >
                      Refresh List
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title="Clear Entire Cache" size="small">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>
                    Clear the entire selected cache including all its keys.
                  </Text>
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleClearCache}
                    disabled={!selectedCache}
                    block
                  >
                    Clear Cache
                  </Button>
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title="Clear Cache Key" size="small">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Input
                    placeholder="Enter cache key"
                    value={cacheKey}
                    onChange={(e) => setCacheKey(e.target.value)}
                    disabled={!selectedCache}
                  />
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleClearCacheKey}
                    disabled={!selectedCache || !cacheKey}
                    block
                  >
                    Clear Key
                  </Button>
                </Space>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Clear All Caches" size="small">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text type="warning">
                    Warning: This will clear all caches in the system. This
                    action cannot be undone.
                  </Text>
                  <Button
                    type="primary"
                    danger
                    icon={<ClearOutlined />}
                    onClick={handleClearAllCaches}
                    block
                  >
                    Clear All Caches
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

export default CacheManagementPage;
